import type { AssetData } from '../../types'

const PREFIX = 'profile:asset-data:'
/** Unscoped key from before caches were tied to the signed-in user. */
const LEGACY_KEY = 'profile:asset-data'

export function profileCacheKey(assetId: string) {
  return `${PREFIX}${assetId}`
}

/** Keycloak may deliver the claim as a string or a number. */
export function normalizeAssetId(value: unknown): string | undefined {
  if (typeof value === 'string' && value) return value
  if (typeof value === 'number' && Number.isFinite(value)) return String(value)
  return undefined
}

function callerMatches(data: AssetData, assetId: string) {
  return data.caller?.assetId === assetId
}

/** Last profile for this user, or undefined when missing, unreadable, or someone else's. */
export function readCachedAssetData(assetId: string | undefined): AssetData | undefined {
  if (!assetId) return undefined
  try {
    const raw = localStorage.getItem(profileCacheKey(assetId))
    if (!raw) return undefined
    const parsed = JSON.parse(raw) as AssetData
    return callerMatches(parsed, assetId) ? parsed : undefined
  } catch {
    return undefined
  }
}

export function writeCachedAssetData(assetId: string, data: AssetData) {
  if (!callerMatches(data, assetId)) return
  localStorage.setItem(profileCacheKey(assetId), JSON.stringify(data))
}

/** Drop every per-user profile mirror, including the old unscoped key. */
export function clearProfileCache() {
  const keys: string[] = []
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i)
    if (key === LEGACY_KEY || key?.startsWith(PREFIX)) keys.push(key)
  }
  for (const key of keys) localStorage.removeItem(key)
}

export function discardLegacyProfileCache() {
  localStorage.removeItem(LEGACY_KEY)
}

/** True for a profile query that belongs to a different signed-in user. */
export function isForeignProfileQuery(queryKey: readonly unknown[], assetId: string | undefined) {
  return queryKey[0] === 'profile' && queryKey[1] === 'asset-data' && queryKey[2] !== (assetId ?? null)
}
